// @ts-nocheck
/**
 * @file Primary_Button.tsx
 * @module Components/Button
 * @description The single button primitive for the entire app (originally from
 * sk-voice-fe, extended into a superset).
 *
 * Two design needs from one component:
 *   • Premium CTAs (sizes `large` / `small`) — tall, uppercase, full-width.
 *   • Dense console buttons (sizes `lg` / `md` / `sm` / `xs` / `icon`) —
 *     compact, sentence-case, hug-content; used in toolbars, cards, table rows.
 *
 * Variants: primary | default | accent | outline | danger | cancel | ghost | google.
 * Pass `title` for a labelled button, `children` for custom content, and
 * `leftIcon` only (with `size="icon"`) for an icon-only button.
 */

import React, { useState } from "react";
import {
  TouchableOpacity,
  Text,
  ActivityIndicator,
  StyleSheet,
  StyleProp,
  ViewStyle,
  TextStyle,
  View,
  Platform,
} from "react-native";
import { Colors } from "../../theme/tokens";

/* ================= TYPES ================= */

export type ButtonVariant =
  | 'primary' | 'default' | 'accent' | 'outline'
  | 'danger' | 'cancel' | 'ghost' | 'google';
export type ButtonSize = 'large' | 'small' | 'lg' | 'md' | 'sm' | 'xs' | 'icon';

// Per-size geometry + type treatment. `stretch` decides whether the button
// fills its row (premium CTAs) or hugs its content (dense console buttons).
// `dense` flattens the heavy CTA shadow so toolbar buttons read flat.
const SIZES: Record<ButtonSize, {
  minHeight: number; width?: number; borderRadius: number;
  paddingVertical: number; paddingHorizontal: number;
  fontSize: number; letterSpacing: number; uppercase: boolean;
  stretch: boolean; dense: boolean;
}> = {
  large: { minHeight: 62, borderRadius: 2, paddingVertical: 12, paddingHorizontal: 20, fontSize: 14, letterSpacing: 1.5, uppercase: true,  stretch: true,  dense: false },
  small: { minHeight: 40, borderRadius: 2, paddingVertical: 6,  paddingHorizontal: 14, fontSize: 12, letterSpacing: 0.5, uppercase: true,  stretch: false, dense: false },
  lg:    { minHeight: 38, borderRadius: 2, paddingVertical: 0,  paddingHorizontal: 18, fontSize: 15, letterSpacing: 0.3, uppercase: false, stretch: false, dense: true  },
  md:    { minHeight: 34, borderRadius: 2, paddingVertical: 0,  paddingHorizontal: 14, fontSize: 13, letterSpacing: 0.3, uppercase: false, stretch: false, dense: true  },
  sm:    { minHeight: 28, borderRadius: 2, paddingVertical: 0,  paddingHorizontal: 10, fontSize: 12, letterSpacing: 0.2, uppercase: false, stretch: false, dense: true  },
  xs:    { minHeight: 22, borderRadius: 2, paddingVertical: 0,  paddingHorizontal: 8,  fontSize: 11, letterSpacing: 0.2, uppercase: false, stretch: false, dense: true  },
  icon:  { minHeight: 32, width: 32, borderRadius: 2, paddingVertical: 0, paddingHorizontal: 0, fontSize: 13, letterSpacing: 0, uppercase: false, stretch: false, dense: true },
};

// Filled variants get a white spinner / label and a drop shadow on CTA sizes.
const FILLED: Record<string, boolean> = { primary: true, accent: true, danger: true, google: true };

// Per-variant colors. Dense-friendly variants use app tokens so the migrated
// console buttons keep the look they had as <Btn>.
const VARIANTS: Record<ButtonVariant, { bg: string; text: string; border: string }> = {
  primary: { bg: '#0A0A0A',     text: '#FFFFFF',   border: '#0A0A0A' },
  default: { bg: Colors.card,   text: Colors.ink,  border: Colors.line },
  accent:  { bg: Colors.accent, text: '#FFFFFF',   border: Colors.accent },
  danger:  { bg: Colors.danger, text: '#FFFFFF',   border: Colors.danger },
  outline: { bg: 'transparent', text: Colors.ink2, border: Colors.line },
  ghost:   { bg: 'transparent', text: Colors.ink2, border: 'transparent' },
  cancel:  { bg: 'transparent', text: '#EF4444',   border: '#EF4444' },
  google:  { bg: '#4285F4',     text: '#FFFFFF',   border: '#4285F4' },
};

type PrimaryButtonProps = Readonly<{
  title?: string;
  children?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  textTransform?: 'none' | 'capitalize' | 'uppercase' | 'lowercase';
  /** Suppress the hover background fill (for inline text links where the paper
   *  box looks like a glitch). The text/color hover still applies. */
  noHoverBg?: boolean;
  testID?: string;
}>;

/* ================= COMPONENT ================= */

export default function PrimaryButton({
  title,
  children,
  loading = false,
  disabled = false,
  onPress,
  variant = 'primary',
  size = 'large',
  style,
  textStyle,
  leftIcon,
  rightIcon,
  textTransform,
  noHoverBg = false,
  testID,
}: PrimaryButtonProps) {
  const isInactive = disabled || loading;
  const [hovered, setHovered] = useState(false);

  const sz = SIZES[size] ?? SIZES.large;
  const v = VARIANTS[variant] ?? VARIANTS.primary;
  const filled = !!FILLED[variant];
  const spinnerColor = filled ? '#fff' : Colors.ink;

  // Non-filled variants get a subtle paper hover; google darkens. Inline text
  // links opt out via `noHoverBg` so they don't show a boxy fill on hover.
  let bg = v.bg;
  if (hovered && !isInactive) {
    if (variant === 'google') bg = '#1A73E8';
    else if (!filled && !noHoverBg) bg = Colors.paper2;
  }

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={isInactive}
      activeOpacity={0.82}
      testID={testID}
      style={[
        styles.base,
        {
          minHeight: sz.minHeight,
          width: sz.width,
          borderRadius: sz.borderRadius,
          paddingVertical: sz.paddingVertical,
          paddingHorizontal: sz.paddingHorizontal,
          alignSelf: sz.stretch ? 'stretch' : 'flex-start',
          backgroundColor: bg,
          borderColor: v.border,
          borderWidth: (variant === 'outline' || variant === 'cancel') ? 1.5 : 1,
        },
        filled && !sz.dense && styles.filledShadow,
        style,
        isInactive && styles.disabled,
      ]}
      {...(Platform.OS === 'web' ? {
        onMouseEnter: () => setHovered(true),
        onMouseLeave: () => setHovered(false),
      } : {})}
    >
      <View style={styles.contentWrapper}>
        {loading ? (
          <ActivityIndicator color={spinnerColor} size="small" />
        ) : (
          <View style={styles.inner}>
            {leftIcon && <View style={styles.iconLeft}>{leftIcon}</View>}
            {(title ?? children) != null && (
              <Text style={[
                styles.text,
                {
                  color: isInactive ? '#94A3B8' : v.text,
                  fontSize: sz.fontSize,
                  letterSpacing: sz.letterSpacing,
                },
                sz.uppercase ? styles.textCaps : styles.textNoCaps,
                textTransform && { textTransform },
                textStyle,
              ]}>
                {title ?? children}
              </Text>
            )}
            {rightIcon && <View style={styles.iconRight}>{rightIcon}</View>}
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  base: {
    justifyContent: "center",
    alignItems: "center",
    ...(Platform.OS === 'web' ? { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' } as any : {}),
  },
  filledShadow: {
    shadowColor: '#0A0A0A',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.25,
    shadowRadius: 20,
    elevation: 8,
    ...Platform.select({ web: { boxShadow: '0 10px 20px rgba(0,0,0,0.25)' } as any }),
  },
  disabled: {
    backgroundColor: "#CBD5E1",
    borderColor: "#E2E8F0",
    elevation: 0,
    shadowOpacity: 0,
    ...Platform.select({ web: { boxShadow: 'none' } as any }),
  },
  contentWrapper: { width: "100%", justifyContent: "center", alignItems: "center" },
  inner: { flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 8 },
  iconLeft: { flexDirection: "row", alignItems: "center" },
  iconRight: { flexDirection: "row", alignItems: "center" },
  text: { textAlign: "center" },
  textCaps: { textTransform: "uppercase", fontWeight: "800" },
  textNoCaps: { textTransform: "none", fontWeight: "600" },
});
